<template>
  <button
    v-on-click-outside="() => (showMenu = false)"
    type="button"
    :disabled="!statuses?.length || viewOnly"
    :class="status || 'not-selected'"
    class="flex items-center justify-center space-x-2 rounded-full px-3 py-1.5 text-xxs focus:outline-hidden"
    @click.stop="showMenu = !showMenu"
  >
    <span class="uppercase">
      {{ status || placeholder }}
    </span>
    <div v-if="showInfoIcon" v-tooltip="errorDescription">
      <icon-base
        icon="info-small"
        view-box="0 0 14 14"
        class="translate-y-0-25 transform text-red-400"
      />
    </div>
    <span
      v-if="statuses?.length && !viewOnly"
      class="inset-y-0 flex cursor-pointer place-self-center"
    >
      <icon-base height="6" width="10" icon="dropdown-arrow" />
    </span>
  </button>

  <ul
    v-if="!viewOnly && showMenu && statuses?.length"
    ref="menuElement"
    class="z-50 w-26 rounded-md bg-white py-2 text-sm text-gray-500 shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:w-40 sm:text-sm"
  >
    <li
      v-for="(status, index) in statuses"
      :key="index"
      class="flex justify-start overflow-auto py-2 pr-2 hover:bg-gray-200"
      role="option"
      :aria-selected="modelValue == status"
      @click="selectStatus(status)"
    >
      <span class="ml-3 cursor-pointer font-normal">{{ status }}</span>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import last from "lodash/last";

import { stringIsJson } from "@/helpers/formatting";
import { useTeleportV2 } from "@/hooks/elements";

import IconBase from "../ui/IconBase.vue";

import type { ILenderPlacement } from "@/models/funders";
import type { PropType } from "vue";

const STATUS_ERROR = "error";

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: "",
  },
  statuses: {
    type: Array as PropType<string[]>,
  },
  status: String,
  model: {
    type: Object as PropType<ILenderPlacement>,
  },
  viewOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "statusSelected"]);

const placeholder = "status";

const { t } = useI18n();

const showMenu = ref(false);
const menuElement = ref<HTMLUListElement | null>(null);

useTeleportV2({
  target: menuElement,
  to: "#modals",
  offset: { top: 5 },
});

const showInfoIcon = computed(
  () => props.status?.toLowerCase() === STATUS_ERROR
);

/**
 * Note: the response that's included inside the placement is ambiguous
 * therefore there are multiple type overrides of the response values
 * (different services return different response bodies that are not consistent when returned from api)
 */
const errorDescription = computed(() => {
  const response = last(props.model?.responses || [])?.response || null;

  if (!response) {
    return props.model?.decline_reason || t("COMMON.ERROR_MSG_UNAVAILABLE");
  }

  if (typeof response === "string") {
    if (!stringIsJson(response)) {
      return response;
    }
    const parsed = JSON.parse(response);
    if (parsed["validation"]) {
      return Object.values(parsed["validation"])
        .map((val) => {
          if (typeof val !== "object") {
            return val;
          }
          return Object.values(val || {}).join(", ");
        })
        .flat()
        .join(", ");
    }
    if (parsed["errors"]["validation_errors"]) {
      return Object.values(parsed["errors"]["validation_errors"])
        .flat()
        .join(", ");
    }
    if (parsed["message"]) {
      const msg: string = parsed["error"];
      return msg;
    }
  }

  if (typeof response === "object") {
    if (response["message"]) {
      const msg = response["message"] as string;
      return msg;
    }

    const validationErrors = (
      response["errors"] as {
        validation_errors?: Array<Record<string, string>>;
      }
    )?.["validation_errors"];

    if (validationErrors?.length) {
      return validationErrors
        .map((validationErrs) =>
          Object.values(validationErrs).flat().join(", ")
        )
        .join(", ");
    }

    const nationalFundingError = (
      response["ResponseStatus"] as {
        ErrorInfo?: {
          Message?: string;
        };
      }
    )?.ErrorInfo?.Message;

    if (nationalFundingError) {
      return nationalFundingError;
    }

    const hasLeadErrors = Object.keys(response || {}).some((key) =>
      Object.keys(((response as Record<string, string>) || {})[key])?.includes(
        "Errors"
      )
    );
    if (hasLeadErrors) {
      const msgArray = Object.values(response).map((errorObject) =>
        Object.values(
          (
            errorObject as Record<
              string,
              Record<string, Record<string, unknown>>
            >
          )?.["Errors"] || {}
        )
      );
      const msg =
        msgArray
          ?.flat()
          .map((e) => e["Message"])
          .join(" ,") || "";

      if (msg) {
        return msg;
      }
    }
  }

  return props.model?.decline_reason || t("COMMON.ERROR_MSG_UNAVAILABLE");
});

const selectStatus = (status: string) => {
  showMenu.value = false;
  emit("update:modelValue", status);
  emit("statusSelected", status);
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.Pending {
  background-color: rgba(255, 159, 4, 0.1);
  color: rgb(255, 159, 4);
}

.Approved {
  background-color: rgba(6, 214, 160, 0.1);
  color: rgb(6, 214, 160);
}

.Declined {
  background-color: rgba(255, 89, 94, 0.1);
  color: rgb(255, 89, 94);
}

.Error {
  background-color: rgba(255, 89, 94, 0.1);
  color: rgb(255, 89, 94);
}

.not-selected {
  background-color: rgb(243, 244, 246);
}
</style>
